import { customTheme } from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from './authenticatedPageWrapper';

// Card shadows are tinted with the darkest brand shade; unbranded, that is today's Aslan indigo.
const shadowTint = (alpha: number) => `rgba(var(--aslan-shadow-rgb, 46, 35, 100), ${alpha})`;
// The brand colour at an opacity; unbranded, that is today's Aslan purple.
const brandAlpha = (alpha: number) => `rgba(var(--aslan-brand-rgb, 89, 52, 196), ${alpha})`;

const theme = customTheme({
  // Portal colours that the shared palette does not carry, as semantic tokens alongside the
  // library's own (brand.subtle, action.primary, ...). Each default is the Aslan colour, so an
  // unbranded portal is unchanged; the purples follow a company's brand by overriding their
  // variable in the company's co-brand stylesheet (see contexts/Branding), and the greys never do.
  semanticTokens: {
    colors: {
      /** Primary indigo: the active nav pill, form primaries, selected chips. */
      'brand.indigo': '#534AB7',
      /** The brighter indigo used by the filter toolbars and coverage chips. */
      'brand.indigoAccent': '#5B3DE8',
      /** Ground for the page heroes. */
      'brand.heroPurple': '#2E2364',
      /** The employee sidebar rail. */
      'brand.sidebarLilac': '#EFEDFB',
      /** Avatar ground: soft enough to take dark initials. */
      'brand.avatarLilac': '#C0B2F7',
      /** Panel ground behind white cards, and its hairline. */
      'brand.panel': '#F7F6FB',
      'brand.panelBorder': '#E4E2EE',
      /**
       * Toolbar ground where a toolbar sits on the page rather than in a panel. Follows the
       * brand's lightest artwork shade, which every co-brand stylesheet already sets.
       */
      'brand.toolbar': 'var(--aslan-artwork-subtle, #F4F3FB)',
      /** Menu text and the muted icons beside it: the labels carry the meaning. */
      'brand.menuText': '#4A4958',
      'brand.mutedIcon': '#7A7788',
      /** Body copy inside cards. */
      'brand.bodyText': '#5F5E6E',
      /** Hairline around Roar cards and their grid. */
      'brand.cardHairline': '#ECEAF3',
      /** The eligibility callout's ground and hairline. */
      'brand.calloutTint': '#FAFAFD',
      'brand.calloutHairline': '#EDEBF3',
      /** Ground for an optional step's badge in the recognise form. */
      'brand.optionalLilac': '#EDEBF8',
      /** The country picker's muted rows and the ink of the hovered or chosen one. */
      'brand.countryMuted': '#ACB9C4',
      'brand.countryInk': '#396070',
      /** Ground of the minimum top-up tag: teal, so it never follows the brand. */
      'brand.mintTint': '#CEF4F0',
      /** Muted nav text, dividers and hover grounds on the sidebars; follows the brand. */
      'brand.navMuted': '#C8BEEB',
      /** The brand colour at a light opacity: drop zones, spinner tracks, selected tiles. */
      'brand.tint10': brandAlpha(0.1),
      'brand.tint20': brandAlpha(0.2),
      'brand.tint30': brandAlpha(0.3),
      'brand.tint40': brandAlpha(0.4),
    },
  },
  shadows: {
    card: `0 2px 8px ${shadowTint(0.07)}`,
    cardRaised: `0 4px 14px ${shadowTint(0.12)}`,
    hero: `0 1px 3px ${shadowTint(0.05)}`,
    /** Neutral, so a branded benefit card lifts the same way. */
    benefitRaised: '0 6px 20px rgba(25, 25, 39, 0.08)',
    /** Admin tables sit under a purple-slate shadow, tinted like the cards when branded. */
    table: '0 4px 12px 0 rgba(var(--aslan-shadow-rgb, 44, 41, 72), 0.10)',
    /** Blue-grey, neutral: the file-type modal, its tiles and the stat cards. */
    modal: '0 14px 28px 0 rgba(36, 54, 70, 0.10)',
    tile: '0 12px 22px 0 rgba(36, 54, 70, 0.06)',
    stat: '0px 12px 11px rgba(36, 54, 70, 0.06)',
    /** Black, neutral: the country menu and the leaderboard tabs. */
    menu: '0px 1px 30px rgba(0, 0, 0, 0.1)',
    tabs: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  components: {
    AuthenticatedPageWrapper,
    Button: {
      variants: {
        outline: {
          _hover: {
            _disabled: {
              bg: 'brand.lightGray1',
              color: 'system.disabled',
              borderColor: 'system.disabled',
              opacity: 1,
            },
          },
        },
      },
    },
  },
  fonts: {
    heading: 'MontserratVariable, sans-serif',
    body: 'MontserratVariable, sans-serif',
  },
});

export default theme;
